import { useCallback, useEffect, useRef, useState } from 'react'
import Big from 'big.js'

import csrfHeaders from 'src/utils/csrfHeaders'
import blackbaudAppearance from './blackbaudAppearance'
import recurrenceDescription from '../donate_v6/fiat/recurrenceDescription'
import { genericError } from './useRecord'

// Blackbaud Checkout for v7, using the SDK's `inline` workflow mode — the same
// mode v6 runs. The composite iframe carries Blackbaud's method tabs, the card
// or bank fields, and its cover-the-fees control. We construct it for the
// donor's chosen method only — Card carries Blackbaud's wallet tabs, Bank is on
// its own — and picking a different method rebuilds the workflow.
//
// Mounted at App level, not inside the payment panel: Panel renders
// `{status && children}`, so the panel unmounts as the donor navigates and the
// workflow would go with it (see BlackbaudSlot).

const scriptPromises = {}

const loadScript = url => {
  if (window.BlackbaudCheckout) return Promise.resolve()
  if (scriptPromises[url]) return scriptPromises[url]

  scriptPromises[url] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${url}"]`)
    const script = existing || document.createElement('script')

    const onError = error => {
      // Don't cache a failed load (network blip, blocked request, CSP race) and
      // drop the dead tag, so a later mount can retry from scratch instead of
      // being permanently blocked until a full page reload.
      delete scriptPromises[url]
      script.remove()
      reject(error)
    }

    script.addEventListener('load', resolve, { once: true })
    script.addEventListener('error', onError, { once: true })

    if (!existing) {
      script.src = url
      script.async = true
      document.head.appendChild(script)
    }
  })

  return scriptPromises[url]
}

// ?bbdebug=1 prints the live bbcheckout-* protocol.
const debugEnabled = () => {
  try {
    return new URLSearchParams(window.location.search).has('bbdebug')
  } catch {
    return false
  }
}

const debugProtocol = instance => {
  if (!debugEnabled()) return

  window.__bbCheckout = instance

  window.addEventListener('message', event => {
    const type = event.data?.messageType
    if (typeof type === 'string' && type.startsWith('bbcheckout-'))
      console.info('[bb]', type, event.data.details ?? '')
  })

  console.info('[bb] workflow created', debugState(instance))
}

const debugState = instance => ({
  paymentMode: instance.paymentMode,
  isRecurring: instance.isRecurring,
  buttonEnabled: instance.completeCheckoutButtonComponent?.enabled,
  baseAmount: instance.baseAmount,
  checkoutSessionId: instance.checkoutSessionId,
})

// Card rides with Apple Pay and Google Pay, so Blackbaud's own tab strip shows
// whichever wallets it can actually render (device, domain verification) — we
// don't predict that. Bank stays on its own.
const paymentMethodOptionsFor = (method, base) => ({
  ...base,
  card: { enabled: method === 'credit_card' },
  directDebit: { enabled: method === 'bank' },
  wallets: {
    applePayEnabled: method === 'credit_card',
    googlePayEnabled: method === 'credit_card',
    amazonPayEnabled: false,
  },
})

const amountToCents = ({ amount, currency }) =>
  parseInt(Big(amount || 0).times(currency?.subunit_to_unit || 1).round(0).toString())

const subscription = (observable, handler) =>
  observable?.subscribe ? observable.subscribe(handler) : null

const eventError = (evt, i18n) => ({
  base: [ evt?.error?.text || i18n.t('errors.donation_error') ],
})

const useBlackbaudCheckout = ({
  i18n, donation, record, partnerItem, paymentMethod,
  blackbaudCheckoutProps, finished, setSubmitInFlight, enabled,
}) => {
  const [ checkout, setCheckout ] = useState(null)
  const [ ready, setReady ] = useState(false)
  const [ processing, setProcessing ] = useState(false)
  const [ buttonLocked, setButtonLocked ] = useState(false)
  const [ errors, setErrors ] = useState(null)
  const [ amountDetails, setAmountDetails ] = useState(null)
  // Bumped by reset(), so "Donate again" gets a fresh workflow instead of one
  // whose session already completed a transaction.
  const [ generation, setGeneration ] = useState(0)

  // Server gate, so a stale cadence can't turn recurring on by itself.
  const recurring = !!blackbaudCheckoutProps?.recurringEnabled &&
    donation.state.subscription !== 'one_time'

  const checkoutRef = useRef(null)
  const transactionRef = useRef(null)
  const completingRef = useRef(false)
  const buttonLockedRef = useRef(false)
  // The only report of which method the donor actually used — Blackbaud's
  // transactions carry no method field, so the server can't derive it after the
  // fact.
  const paymentModeRef = useRef(null)
  const latestRef = useRef({})

  // handleCheckoutComplete and finalize run from a subscription captured on the
  // first render, so anything of theirs that can change between renders MUST be
  // read from here rather than closed over — a direct prop read in either would
  // silently go stale (e.g. finish a donation against the amount the donor first
  // landed on).
  latestRef.current = {
    blackbaudCheckoutProps, donation, finished, i18n, record, partnerItem,
    paymentMethod, setSubmitInFlight,
  }

  // Blackbaud's button tracks its own enabled flag and defaults to disabled,
  // silently dropping clicks until we flip it on.
  const setCheckoutButtonEnabled = value => {
    const button = checkoutRef.current?.completeCheckoutButtonComponent
    if (button) button.enabled = !!value
  }

  const lockCheckoutButton = () => {
    buttonLockedRef.current = true
    setButtonLocked(true)
    setCheckoutButtonEnabled(false)
  }

  const resetCheckoutButton = () => {
    completingRef.current = false
    buttonLockedRef.current = false
    setButtonLocked(false)
    setProcessing(false)
    setCheckoutButtonEnabled(true)
  }

  // "Donate again" starts a fresh attempt against the same workflow. The
  // success path leaves the button locked on purpose, so without this the next
  // donation reaches the panel with a disabled, spinning submit.
  const reset = () => {
    resetCheckoutButton()
    transactionRef.current = null
    setErrors(null)
    setGeneration(g => g + 1)
  }

  // Blackbaud's own listener on our button is what authorizes, and it's bound in
  // the bubble phase. We take the click in capture purely to veto it once the
  // money has moved — capture runs first regardless of registration order, so
  // stopImmediatePropagation keeps their handler from ever seeing it.
  //
  // Reads refs rather than state so the identity stays stable: re-adding the
  // listener on every render would race their binding.
  const handleButtonClick = useCallback(event => {
    if (buttonLockedRef.current || completingRef.current) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }, [])

  const syncCheckout = () => {
    const instance = checkoutRef.current
    if (!instance) return

    // First, so a later setter throwing can't leave a monthly donor one-time.
    instance.isRecurring = recurring
    instance.recurrenceDescription = recurring
      ? recurrenceDescription({ i18n, subscription: donation.state.subscription })
      : ''

    const cents = amountToCents({ amount: donation.total, currency: donation.currency })
    // The SDK throws below 50 cents. The Start step's min-amount validation
    // normally keeps us clear of that, but a partner minimum below $0.50 (or a
    // zero total mid-edit) would otherwise take the whole panel down.
    if (cents >= 50) instance.baseAmount = cents

    // Blackbaud's schema is a single `name`; firstName/lastName aren't keys it
    // reads, so they prefill nothing.
    instance.billingDetails = {
      name: [ donation.state.firstName, donation.state.lastName ].filter(Boolean).join(' '),
      email: donation.state.email,
      phone: donation.state.phone,
    }
  }

  // Blackbaud's button authorizes the card the instant it's clicked, so the
  // draft Order can't exist beforehand. Once we hold an authorized transaction,
  // create the draft and capture against it.
  const finalize = async () => {
    const {
      blackbaudCheckoutProps, donation, finished, i18n, record, partnerItem,
      paymentMethod, setSubmitInFlight,
    } = latestRef.current
    const transaction = transactionRef.current
    if (!transaction) return

    // Clearing these lets the donor click Blackbaud's button again; each click
    // is a fresh authorization.
    const rearm = () => {
      resetCheckoutButton()
      setSubmitInFlight(false)
      transactionRef.current = null
    }

    try {
      const { errors: draftErrors, token, serializedDonation } =
        await record.saveDraft({
          order: {
            // payment_method_used is set server-side at finalize from the
            // payment_mode we send below (see BlackbaudFinalization), so card
            // vs. ACH needs no branch here.
            payment_source: 'blackbaud_checkout',
          },
        })

      if (draftErrors) {
        rearm()
        return setErrors(draftErrors)
      }

      const response = await fetch(blackbaudCheckoutProps.blackbaudCheckoutsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          order_id: token,
          transaction_id: transaction.id,
          authorized_amount_cents: transaction.amountDetails?.total,
          amount_details: transaction.amountDetails,
          last4: transaction.last4,
          frequency: donation.state.subscription,
          payment_mode: transaction.paymentMode,
          locale: i18n.locale,
        }),
      })

      if (response.ok) {
        transactionRef.current = null
        const { state, ...rest } = donation
        return finished({
          ...state,
          ...rest,
          ...serializedDonation,
          token,
          partnerItem,
          paymentMethod,
        })
      }

      const json = await response.json().catch(() => ({}))

      if (response.status >= 500) {
        // The card may already be captured server-side. Do NOT re-arm the
        // button (a fresh click would authorize a second charge) — keep it
        // locked while BlackbaudCheckoutReconciliationJob reconciles.
        transactionRef.current = null
        setProcessing(false)
        setSubmitInFlight(false)
        lockCheckoutButton()
      } else {
        rearm()
      }
      setErrors({ base: [ json.message || i18n.t('errors.donation_error') ] })
    } catch (error) {
      console.error('Error capturing Blackbaud Checkout transaction:', error)
      rearm()
      setErrors(genericError(i18n))
    }
  }

  const handleCheckoutComplete = evt => {
    if (completingRef.current) return

    const { i18n, setSubmitInFlight } = latestRef.current

    // Blackbaud reports the outcome of its own payment attempt; only record the
    // donation when it actually succeeded.
    if (evt?.success === false) {
      resetCheckoutButton()
      return setErrors(eventError(evt, i18n))
    }

    const transaction = evt?.transaction || {}

    // Blackbaud Checkout charges the card itself and returns the transaction id;
    // our backend reads it back and records the donation.
    transactionRef.current = {
      id: transaction.id || evt?.transactionId,
      amountDetails: transaction.amountDetails || evt?.amountDetails,
      last4: transaction.card?.last4 || evt?.cardLast4,
      paymentMode: paymentModeRef.current,
    }

    completingRef.current = true
    lockCheckoutButton()
    setProcessing(true)
    setErrors(null)
    setSubmitInFlight(true)
    finalize()
  }

  // paymentModeChanges is a BehaviorSubject seeded 'none', so it fires on
  // subscribe. Forwarding that seed would beat the server's transaction_type
  // fallback ('none'.presence is truthy) and leave the order mislabelled.
  const handlePaymentModeChange = mode => {
    if (!mode || mode === 'none') return
    paymentModeRef.current = mode
  }

  useEffect(() => {
    if (!enabled || !blackbaudCheckoutProps) return

    let cancelled = false
    let instance
    let subscriptions = []

    loadScript(blackbaudCheckoutProps.scriptUrl)
      .then(() => {
        if (cancelled) return

        instance = window.BlackbaudCheckout({
          workflowMode: 'inline',
          paymentConfigurationId: blackbaudCheckoutProps.paymentConfigurationId,
          applicationId: blackbaudCheckoutProps.applicationId,
          ...(blackbaudCheckoutProps.applicationName
            ? { applicationName: blackbaudCheckoutProps.applicationName } : {}),
          paymentMethodOptions: paymentMethodOptionsFor(
            paymentMethod, blackbaudCheckoutProps.paymentMethodOptions
          ),
          // The offset covers this charge only; later installments wouldn't match.
          feeOffsetType: recurring ? 0 : blackbaudCheckoutProps.feeOffsetType,
          currency: donation.currency.iso_code,
          ...blackbaudAppearance(),
        })
        checkoutRef.current = instance

        subscriptions = [
          // Both fire; checkoutReady lands first. Take whichever arrives.
          subscription(instance.checkoutReady, () => setReady(true)),
          subscription(instance.inlineCheckoutComponent?.ready, () => setReady(true)),
          subscription(instance.checkoutComplete, handleCheckoutComplete),
          subscription(instance.amountDetailsChanges, setAmountDetails),
          subscription(instance.paymentModeChanges, handlePaymentModeChange),
        ].filter(Boolean)

        debugProtocol(instance)
        syncCheckout()
        setCheckout(instance)
      })
      .catch(error => {
        console.error('Error loading Blackbaud Checkout:', error)
        setErrors(genericError(latestRef.current.i18n))
      })

    return () => {
      cancelled = true
      subscriptions.forEach(sub => sub.unsubscribe?.())
      instance?.destroy?.()
      if (checkoutRef.current === instance) checkoutRef.current = null
      transactionRef.current = null
      paymentModeRef.current = null
      setCheckout(null)
      setReady(false)
      setAmountDetails(null)
      // completingRef / buttonLockedRef are deliberately NOT cleared. They mean
      // "Blackbaud may already hold this donor's money", which outlives any one
      // workflow — switching method rebuilds it, and clearing them here would
      // re-arm the button over a capture that may have succeeded. Both are
      // false anyway unless an authorization happened; reset() clears them.
    }
  }, [
    enabled,
    // Rebuilds the workflow when the donor picks a different method, so the
    // composite re-renders with only that one enabled.
    paymentMethod,
    blackbaudCheckoutProps?.scriptUrl,
    blackbaudCheckoutProps?.paymentConfigurationId,
    blackbaudCheckoutProps?.feeOffsetType,
    // feeOffsetType is construction-only. The boolean, so monthly -> quarterly
    // doesn't rebuild.
    recurring,
    generation,
    donation.currency.iso_code,
  ])

  useEffect(() => {
    if (!enabled || !checkoutRef.current) return
    try {
      syncCheckout()
    } catch (error) {
      console.error('Error updating Blackbaud Checkout:', error)
    }
  }, [
    enabled,
    checkout,
    donation.total,
    donation.state.subscription,
    donation.state.firstName,
    donation.state.lastName,
    donation.state.email,
    donation.state.phone,
  ])

  // The composite enforces its own field validity, so the button stays live and
  // Blackbaud rejects a bad card itself. Gated on `ready` because the enabled
  // flag is dropped rather than buffered before the button's iframe exists.
  useEffect(() => {
    setCheckoutButtonEnabled(
      ready && !buttonLockedRef.current && !completingRef.current
    )
  }, [ ready, checkout ])

  // Blackbaud's in-iframe cover-the-fees option reports its fee in cents. Add it
  // to our live base so the button shows what Blackbaud will actually authorize.
  const feeOffsetCents = amountDetails?.donorOffsettingFees
    ? amountDetails.feeOffsetAmount ?? 0
    : 0
  const total = Big(donation.total)
    .plus(Big(feeOffsetCents).div(donation.currency.subunit_to_unit || 1))
    .toFixed(donation.currency.exponent)

  return {
    checkout,
    ready,
    processing,
    buttonLocked,
    errors,
    total,
    handleButtonClick,
    reset,
  }
}

export default useBlackbaudCheckout
